import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import { Box } from "@mui/material";

// Material Dashboard React example components
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";

// Custom components
import ContractualDetailsDrawer from "examples/Drawers/profile/ContractualDetailsDrawer";
import { mapTypeFromDB } from "examples/Drawers/profile/models/TaxRegistration";

// 3rd party library
import moment from "moment";

// Constants from Utils
import Constants, { defaultData, Common } from "utils/Constants";
import { useSelector } from "react-redux";

function ContractualDetailsTab({ profile = {}, tabValue, onUpdate, isMatrixView = false }) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const ConfigData = useSelector((state) => state.config);
  const permission = ConfigData?.screens[11]?.screensInfo?.agreement;
  const { contractualDetail = {} } = profile;

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleInsuranceInContractualDetails = (insuranceObj, docName) => {
    const fromDate = insuranceObj?.fromDate
      ? moment(insuranceObj?.fromDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;
    const toDate = insuranceObj?.toDate
      ? moment(insuranceObj?.toDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;

    return {
      name: `${docName || ""} (${fromDate} - ${toDate})`,
      url: insuranceObj?.url,
      [Constants.MONGOOSE_ID]: insuranceObj?.[Constants.MONGOOSE_ID],
    };
  };

  const formatDrivingLicence = (licenseObj) => {
    const expiryDate = licenseObj?.expiryDate
      ? moment(licenseObj?.expiryDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;
    const issueDate = licenseObj?.issueDate
      ? moment(licenseObj?.issueDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;

    if (licenseObj?.licenseNumber) {
      if (licenseObj?.url) {
        return {
          name: `${licenseObj?.licenseNumber} (${issueDate} - ${expiryDate})`,
          url: licenseObj.url,
          [Constants.MONGOOSE_ID]: licenseObj[Constants.MONGOOSE_ID],
        };
      }
      return `${licenseObj?.licenseNumber} (${issueDate} - ${expiryDate})`;
    }
    return Constants.EMPTY_VALUE;
  };

  const formatSeamansBook = (seamansBookObj) => {
    const fromDate = seamansBookObj?.fromDate
      ? moment(seamansBookObj?.fromDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;
    const toDate = seamansBookObj?.toDate
      ? moment(seamansBookObj?.toDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;

    return {
      name: `${seamansBookObj?.name} (${fromDate} - ${toDate})`,
      url: seamansBookObj?.url,
      [Constants.MONGOOSE_ID]: seamansBookObj?.[Constants.MONGOOSE_ID],
    };
  };

  const formatPassportData = (passportType, passportNumber) => {
    const passportData = contractualDetail?.identityProof?.filter((item) => item[passportType]);

    if (!passportData || passportData.length === 0) {
      return Constants.EMPTY_VALUE;
    }

    let issueDate = passportData[0]?.fromDate;
    let expiryDate = passportData[0]?.toDate;

    const formattedData = passportData.map((item, index) => ({
      name: `Page ${index + 1}`,
      url: item.url,
      issueDate: item.fromDate,
      expiryDate: item.toDate,
      [Constants.MONGOOSE_ID]: item[Constants.MONGOOSE_ID],
    }));

    expiryDate = expiryDate
      ? moment(expiryDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;
    issueDate = issueDate
      ? moment(issueDate).format(defaultData.WEB_DATE_FORMAT)
      : Constants.EMPTY_VALUE;

    return {
      name: contractualDetail[passportNumber]
        ? `${contractualDetail[passportNumber]} (${issueDate} - ${expiryDate})`
        : Constants.EMPTY_VALUE,
      pages: formattedData,
    };
  };

  const getEmploymentTypeLabel = (type) => {
    if (type === Common.INTERNAL_EMPLOYEE_API) return Common.INTERNAL_EMPLOYEE;
    if (type === Common.EXTERNAL_EMPLOYEE_API) return Common.EXTERNAL_EMPLOYEE;
    return type || Constants.EMPTY_VALUE;
  };

  const formatBooleanYesNo = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return Constants.EMPTY_VALUE;
  };

  const formatCompanyRegistrationNumberWithDoc = (companyRegistrationNumber, companyRegDoc) => {
    const name = companyRegistrationNumber || Constants.EMPTY_VALUE;
    const doc = companyRegDoc?.[0];
    if (doc?.url) {
      return {
        name,
        url: doc.url,
        [Constants.MONGOOSE_ID]: doc[Constants.MONGOOSE_ID],
      };
    }
    return name;
  };

  const formatBankAccountWithDoc = (bankAccount, bankAccountDoc) => {
    const accountNumber = bankAccount || Constants.EMPTY_VALUE;
    const doc = bankAccountDoc?.[0];
    if (doc?.url) {
      return {
        name: accountNumber,
        url: doc.url,
        [Constants.MONGOOSE_ID]: doc[Constants.MONGOOSE_ID],
      };
    }
    return accountNumber;
  };

  const formatTaxIdNumbers = (taxIdNumbers) => {
    if (!taxIdNumbers || taxIdNumbers.length === 0) return Constants.EMPTY_VALUE;

    const formattedEntries = taxIdNumbers.map((tax, index) => {
      const typeLabel = tax.type ? mapTypeFromDB(tax.type) : "";
      const number = tax.number || "";
      const displayName = `${typeLabel}: ${number}`;
      const doc = tax.documents?.[0];

      return {
        name: displayName,
        url: doc?.url || "",
        [Constants.MONGOOSE_ID]: doc?.[Constants.MONGOOSE_ID] || `tax-${index}`,
      };
    });

    return {
      name: "", // Empty name since each page will have its own name displayed
      pages: formattedEntries,
    };
  };

  const infoData = isMatrixView
    ? {
        [Common.DATE_OF_BIRTH_LABEL]: contractualDetail?.birthDate
          ? moment.utc(contractualDetail?.birthDate).format(defaultData.WEB_DATE_FORMAT)
          : Constants.EMPTY_VALUE,
        [Common.EMPLOYMENT_TYPE_LABEL]: getEmploymentTypeLabel(contractualDetail?.employmentType),
      }
    : {
        [Common.PASSPORT_ID_LABEL]: formatPassportData("isPrimary", "passport"),
        [Common.SECOND_PASSPORT_ID_LABEL]: formatPassportData("isSecondary", "secondaryPassport"),
        [Common.NATIONAL_ID_LABEL]:
          contractualDetail?.nationalIdentificationNumber || Constants.EMPTY_VALUE,
        [Common.DRIVERS_LICENSE_LABEL]: profile?.drivingLicence
          ? formatDrivingLicence(profile?.drivingLicence?.[0])
          : "No",
        [Common.SEAMANS_BOOKLET_LABEL]:
          profile?.seamansBook?.length > 0
            ? formatSeamansBook(profile?.seamansBook[0])
            : Constants.EMPTY_VALUE,

        [Common.DATE_OF_BIRTH_LABEL]: contractualDetail?.birthDate
          ? moment.utc(contractualDetail?.birthDate).format(defaultData.WEB_DATE_FORMAT)
          : Constants.EMPTY_VALUE,
        [Common.PLACE_OF_BIRTH_LABEL]: contractualDetail?.birthPlace || Constants.EMPTY_VALUE,
        [Common.EMPLOYMENT_TYPE_LABEL]: getEmploymentTypeLabel(contractualDetail?.employmentType),

        ...(contractualDetail?.employmentType === Common.SELF_EMPLOYED && {
          "Does Reynard need to arrange the insurance?": formatBooleanYesNo(
            contractualDetail?.isInsuranceArrangedByReynard
          ),
          [Common.LIABILITY_INSURANCE_LABEL]:
            contractualDetail?.liabilityInsurance?.length > 0
              ? handleInsuranceInContractualDetails(
                  contractualDetail?.liabilityInsurance?.[0],
                  contractualDetail?.liabilityInsuranceId
                )
              : Constants.EMPTY_VALUE,
          [Common.HEALTH_INSURANCE_LABEL]:
            contractualDetail?.healthInsurance?.length > 0
              ? handleInsuranceInContractualDetails(
                  contractualDetail?.healthInsurance?.[0],
                  contractualDetail?.healthInsuranceId
                )
              : Constants.EMPTY_VALUE,
        }),

        ...(contractualDetail?.employmentType !== Common.INTERNAL_EMPLOYEE_API && {
          [Common.COMPANY_NAME_LABEL]: contractualDetail?.companyName || Constants.EMPTY_VALUE,
          [Common.COMPANY_REGISTRATION_NR_LABEL]: formatCompanyRegistrationNumberWithDoc(
            contractualDetail?.companyRegistrationNumber,
            contractualDetail?.companyRegistrationDocument
          ),
          [Common.TAX_ID_NUMBERS_LABEL]: formatTaxIdNumbers(contractualDetail?.taxIdNumbers),
          [Common.COMPANY_EMAIL_LABEL]:
            contractualDetail?.companyEmailAddress || Constants.EMPTY_VALUE,
          [Common.COMPANY_CITY_LABEL]: contractualDetail?.companyCity || Constants.EMPTY_VALUE,
          [Common.COMPANY_COUNTRY_LABEL]:
            contractualDetail?.companyCountry || Constants.EMPTY_VALUE,
          [Common.COMPANY_ADDRESS_LABEL]:
            contractualDetail?.companyAddress || Constants.EMPTY_VALUE,
        }),

        ...(contractualDetail?.employmentType !== Common.EXTERNAL_EMPLOYEE_API && {
          [Common.BANK_NAME_LABEL]: contractualDetail?.bankName || Constants.EMPTY_VALUE,
          [Common.ACCOUNT_HOLDER_LABEL]:
            contractualDetail?.accountHolderName || Constants.EMPTY_VALUE,
          [Common.BANK_ACCOUNT_NR_LABEL]: formatBankAccountWithDoc(
            contractualDetail?.bankAccount,
            contractualDetail?.bankAccountDocument
          ),
          [Common.BIC_SWIFT_LABEL]: contractualDetail?.bicSwift || Constants.EMPTY_VALUE,
        }),
      };

  return (
    <Box>
      <ProfileInfoCard
        key={profile?.[Constants.MONGOOSE_ID]}
        tabChangeValue={tabValue}
        title="Contractual Details"
        info={infoData}
        action={{ route: "", tooltip: "Edit Profile" }}
        shadow={false}
        showEditIcon={!!permission?.update && !isMatrixView}
        onEditClick={handleDrawerOpen}
      />

      <ContractualDetailsDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        data={{
          ...contractualDetail,
          drivingLicence: profile?.drivingLicence,
          seamansBook: profile?.seamansBook,
        }}
        contractualDetailId={contractualDetail?.[Constants.MONGOOSE_ID]}
        userId={profile?.[Constants.MONGOOSE_ID]}
      />
    </Box>
  );
}

ContractualDetailsTab.propTypes = {
  profile: PropTypes.objectOf(PropTypes.any).isRequired,
  tabValue: PropTypes.number.isRequired,
  onUpdate: PropTypes.func,
  isMatrixView: PropTypes.bool,
};

ContractualDetailsTab.defaultProps = {
  onUpdate: null,
  isMatrixView: false,
};

export default ContractualDetailsTab;
